/*
  Bottom-up "curtain" reveal for color-swatch product switches —
  applies automatically to EVERY product with color swatches,
  sitewide, with no per-product setup (previous version was
  hardcoded to one product's handles by request; that restriction is
  removed here, also by request, so any product added later with the
  same swatch markup gets this for free). Nothing here depends on a
  product name, handle, or ID — every path below is gated purely on
  markup/selector shape (does this element look like a color swatch?)
  or, for Case 3, on a real multi-image variant change actually
  occurring.

  This store has (at least) three different ways a "color" can change
  on a page — confirmed by reading the actual markup/handlers, not
  assumed to all be the same thing:

  1) PRODUCT PAGE (PDP) swatch — a product's color options are
     separate Shopify products (not variants of one product), and the
     swatch is a real <a href="/products/{other product}" class=
     "product-color-swatch"> (from a custom_liquid block in
     templates/product.json / templates/product.set-product.json,
     reading product.metafields.custom.color_variants). Clicking it is
     a genuine hard navigation unless intercepted — that navigation
     itself is what used to read as "the image flashes".

  2) COLLECTION PAGE PRODUCT CARD — different markup again: <button
     type="button" class="product-color-swatch" data-product-handle=
     "..."> with no href (from snippets/card-product.liquid), handled
     by an EXISTING, UNMODIFIED, already-working global click listener
     in layout/theme.liquid: it finds the swatch's closest
     .product-card-wrapper, fetches /products/{handle}.js, caches it
     on window.__colorSwatchProductsCache, and calls a local
     applyProductToCard() that swaps the card's <img src>, price,
     title links, etc. — all of that is left completely alone here.
     This file does NOT reimplement or replace any of it; it adds a
     SEPARATE capturing-phase listener (registered before
     theme.liquid's own, which is non-capturing/bubble-phase) that
     snapshots the card's current image and lays a visual curtain
     overlay on top of it, then lets the click continue propagating
     normally to theme.liquid's own handler, which performs its real
     swap completely hidden underneath. Once the curtain finishes, it
     is removed, revealing whatever theme.liquid's own code has
     already correctly put there. Also preloads on pointerenter/
     touchstart, same as the PDP case, into the SAME
     window.__colorSwatchProductsCache theme.liquid's own handler
     already reads from — so a hover/touch before the click (the
     common case on desktop, and still often true on touch devices
     that register touchstart before the click fires) means both the
     curtain's own new-image fetch AND theme.liquid's real swap find a
     warm cache, not just this file's own reveal.

  3) GENUINE SHOPIFY VARIANT color option (same product, a real
     multi-value "Color" option swapping which variant/media is
     selected, via Dawn's native variant-selects + <product-info>
     flow in assets/product-info.js). IMPORTANT CAVEAT: as of this
     writing, no product on this store actually has more than one
     value for its "Color" option — every existing "color change" on
     this store goes through Case 1/2 above (separate linked
     products), not this path. This case is included defensively, by
     explicit request ("whether colors are variants or separate
     linked products"), so that IF a future product uses a real
     multi-value Color variant, it gets the same reveal automatically
     with no extra setup — but it could NOT be live-tested against any
     real product, since none exists to test it against. It is built
     on Dawn's own documented pub/sub extension points (constants.js's
     PUB_SUB_EVENTS, pubsub.js's subscribe/publish — both plain
     top-level declarations in classic scripts, reachable as bare
     identifiers from this file the same way HTMLUpdateUtility is, not
     a private-method patch), specifically optionValueSelectionChange
     (fires the instant an option is clicked, before any fetch — used
     here purely to snapshot the "old" photo and start covering it
     with this file's overlay before Dawn's own fetch+DOM-swap can run,
     so there is no flash during that step either) and variantChange
     (fires once Dawn's own swap has already completed, used here to
     read the new photo and resolve the curtain's reveal — see
     runCurtain's own comment for why it being created from a promise
     that resolves later, after the real swap already finished, still
     works correctly: the overlay fully covers the real image the
     whole time regardless of what's happening underneath it). If the
     option change did not actually change the photo (e.g. a pure size
     change), the curtain is cancelled with no visible effect at all.

  THE EFFECT (shared by all three cases): two cloned <img> elements
  stacked on document.body (so no card re-render or <main>/variant
  swap can ever disturb them), "old" (the photo that was showing, no
  clip-path, always fully visible — what the curtain rises to cover)
  and "new" (the target photo, clip-path animating from
  inset(100% 0 0 0) — a zero-height window pinned to the very bottom
  of the box, nothing visible — to inset(0 0 0 0), the full box).
  inset()'s values are distances inward from each respective edge, so
  collapsing the top- and bottom-inset to the same point (100%, 0%) is
  what makes the start state exactly zero height at the bottom rather
  than needing a separate "hidden" flag; animating only the top value
  down to 0% is what makes the visible window grow upward from the
  bottom as it animates — the "new color fills the garment from the
  feet up" look. Duration 0.5s (was 1s — shortened by request; same
  cubic-bezier(0.65, 0, 0.35, 1) ease, set in assets/color-swatch-
  reveal.css). The new image is always force-loaded and waited on
  before the animation starts, so a slow network just means the old
  photo stays visible a little longer, never a blank frame — and both
  the PDP and card cases now also preload on hover/touch (see Case 2's
  own note above) so in the common case the fetch is already warm
  well before the click, making the start feel instant rather than
  merely non-blank.

  Loaded sitewide from layout/theme.liquid (right after global.js,
  since Cases 1 and 3 both need HTMLUpdateUtility / PUB_SUB_EVENTS /
  subscribe, which that file and constants.js/pubsub.js define as
  plain top-level declarations — classes/consts don't attach to
  `window` in classic scripts the way `var`/function declarations do,
  but they ARE reachable as bare identifiers from any other classic
  script sharing the same document/global scope, which every script
  here is; confirmed directly rather than assumed). Because it's
  sitewide, this script runs on every collection page too, not just
  product pages — which is exactly why the collection-card case
  (Case 2) previously silently did nothing: the script meant to handle
  it had never loaded there.
*/
(function () {
  if (window.ColorSwatchReveal) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ANIMATION_MS = 500;

  // ---------------------------------------------------------------
  // Shared overlay primitives — used by all three cases below.
  // ---------------------------------------------------------------

  function getOverlayLayer() {
    var layer = document.getElementById('color-swatch-reveal-layer');
    if (!layer) {
      layer = document.createElement('div');
      layer.id = 'color-swatch-reveal-layer';
      document.body.appendChild(layer);
    }
    return layer;
  }

  // Copies the box an <img> actually occupies on screen plus its own
  // object-fit/object-position, so a cloned <img> absolutely
  // positioned over that same box frames the photo identically to
  // whatever CSS (card or gallery) is already sizing the real one.
  function snapshotImageBox(imgEl) {
    var rect = imgEl.getBoundingClientRect();
    var cs = getComputedStyle(imgEl);
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      objectFit: cs.objectFit,
      objectPosition: cs.objectPosition,
    };
  }

  function makeCloneImg(src, box, modifierClass) {
    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.className = 'color-swatch-reveal__img ' + modifierClass;
    img.style.left = box.left + 'px';
    img.style.top = box.top + 'px';
    img.style.width = box.width + 'px';
    img.style.height = box.height + 'px';
    img.style.objectFit = box.objectFit;
    img.style.objectPosition = box.objectPosition;
    return img;
  }

  function loadImage(src) {
    return new Promise(function (resolve) {
      if (!src) {
        resolve(false);
        return;
      }
      var img = new Image();
      var done = false;
      var finish = function (ok) {
        if (done) return;
        done = true;
        resolve(ok);
      };
      img.onload = function () {
        finish(true);
      };
      img.onerror = function () {
        finish(false);
      };
      img.src = src;
      if (img.complete && img.naturalWidth > 0) {
        setTimeout(function () {
          finish(true);
        }, 0);
      }
      // Never hang the reveal on a slow/broken image forever — the old
      // photo clone is already showing the whole time regardless, so
      // this is never a blank frame even if it fires.
      setTimeout(function () {
        finish(false);
      }, 4000);
    });
  }

  // Runs one curtain reveal keyed by `key` (used as a Map key so
  // unrelated reveals — e.g. two different cards, or a card vs. the
  // PDP — never cancel each other, while a second reveal on the SAME
  // key cleanly cancels/replaces the first instead of stacking).
  // oldSrc/box describe the current photo; newSrcPromise resolves to
  // the photo URL to reveal — it's fine for this to resolve well
  // after the real underlying swap already happened (Case 3 does
  // exactly that), since the overlay fully covers the real image for
  // its whole lifetime regardless of what's happening underneath.
  // Resolving to a falsy value (no real change, e.g. a pure size
  // option) cleanly tears the overlay down with no visible effect,
  // rather than leaving a stale "old" clone stuck on screen forever.
  var activeReveals = new Map();

  function runCurtain(key, oldSrc, box, newSrcPromise) {
    var existing = activeReveals.get(key);
    if (existing) existing.cancel();

    if (REDUCED_MOTION.matches) {
      // No overlay at all — whatever already performs the real swap
      // just shows its result instantly, which is correct here.
      return { cancel: function () {} };
    }

    var layer = getOverlayLayer();
    var oldImg = makeCloneImg(oldSrc, box, 'color-swatch-reveal__img--old');
    layer.appendChild(oldImg);

    var cancelled = false;
    var newImg = null;
    var onEnd = null;
    var fallbackTimer = null;

    function cleanup() {
      if (oldImg.parentNode) oldImg.parentNode.removeChild(oldImg);
      if (newImg && newImg.parentNode) newImg.parentNode.removeChild(newImg);
      if (newImg && onEnd) newImg.removeEventListener('transitionend', onEnd);
      clearTimeout(fallbackTimer);
      if (activeReveals.get(key) === controller) activeReveals.delete(key);
    }

    var controller = {
      cancel: function () {
        if (cancelled) return;
        cancelled = true;
        cleanup();
      },
    };
    activeReveals.set(key, controller);

    newSrcPromise.then(function (newSrc) {
      if (cancelled) return;
      if (!newSrc) {
        // No real change after all — tear down the "old" clone instead
        // of leaving it stuck on screen forever.
        controller.cancel();
        return;
      }
      return loadImage(newSrc).then(function () {
        if (cancelled) return;

        newImg = makeCloneImg(newSrc, box, 'color-swatch-reveal__img--new');
        layer.appendChild(newImg);

        // Force a layout flush before adding the "revealed" class so
        // the browser actually animates the clip-path transition
        // instead of jumping straight to its end state.
        // eslint-disable-next-line no-unused-expressions
        newImg.getBoundingClientRect();

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (cancelled) return;
            newImg.classList.add('is-revealed');
          });
        });

        onEnd = function () {
          if (!cancelled) cleanup();
        };
        newImg.addEventListener('transitionend', onEnd);
        fallbackTimer = setTimeout(onEnd, ANIMATION_MS + 400);
      });
    });

    return controller;
  }

  // ---------------------------------------------------------------
  // Case 1: PDP swatch links (<a href>, real separate-product swap).
  // Runs on every product page — gated purely by the selector
  // (.product-color-swatch[href] existing at all), not by handle.
  // ---------------------------------------------------------------
  var FEATURED_IMG_SELECTOR =
    'media-gallery ul li[data-media-id].is-active .product__media img, media-gallery ul li[data-media-id] .product__media img';
  var preloadCache = new Map();
  var pendingTargetHref = window.location.href;
  var currentImageUrlHint = null;
  var PDP_KEY = {}; // unique object identity used purely as a Map key

  var findActiveGalleryImg = function (root) {
    return (root || document).querySelector(FEATURED_IMG_SELECTOR);
  };

  var fetchProductPage = function (href) {
    if (preloadCache.has(href)) return preloadCache.get(href).promise;

    var entry = { mainImageUrl: null, parsedDoc: null };
    var promise = fetch(href, { credentials: 'same-origin' })
      .then(function (response) {
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        entry.parsedDoc = doc;
        var img = findActiveGalleryImg(doc);
        entry.mainImageUrl = img ? img.currentSrc || img.src : null;
        var extraImgs = doc.querySelectorAll('media-gallery ul li[data-media-id] .product__media img');
        for (var i = 0; i < extraImgs.length && i < 4; i++) {
          var src = extraImgs[i].currentSrc || extraImgs[i].src;
          if (src) new Image().src = src;
        }
        return entry;
      })
      .catch(function (err) {
        entry.error = err;
        return entry;
      });

    preloadCache.set(href, { promise: promise, mainImageUrl: null });
    promise.then(function (resolved) {
      var cached = preloadCache.get(href);
      if (cached) cached.mainImageUrl = resolved.mainImageUrl;
    });
    return promise;
  };

  var swapPageContent = function (parsedDoc, targetHref) {
    var newMain = parsedDoc.querySelector('main');
    var oldMain = document.querySelector('main');
    if (!newMain || !oldMain) return;

    HTMLUpdateUtility.viewTransition(oldMain, newMain, [], [
      function () {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      },
    ]);

    var newTitle = parsedDoc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;
    window.history.pushState({}, '', targetHref);
  };

  var runPdpReveal = function (swatchLink) {
    var href = swatchLink.getAttribute('href');
    var targetUrl = new URL(href, window.location.href);

    if (targetUrl.href === pendingTargetHref) return true; // already there / already in flight
    pendingTargetHref = targetUrl.href;

    var activeImg = findActiveGalleryImg(document);
    if (!activeImg) return false; // unexpected markup — fail safe to real navigation

    var box = snapshotImageBox(activeImg);
    var oldSrc = currentImageUrlHint || activeImg.currentSrc || activeImg.src;
    var fetchPromise = fetchProductPage(href);

    if (REDUCED_MOTION.matches) {
      fetchPromise.then(function (entry) {
        if (entry.parsedDoc) {
          currentImageUrlHint = entry.mainImageUrl || currentImageUrlHint;
          swapPageContent(entry.parsedDoc, href);
        } else {
          window.location.href = href;
        }
      });
      return true;
    }

    var newSrcPromise = fetchPromise.then(function (entry) {
      if (!entry.parsedDoc) {
        window.location.href = href;
        return null;
      }
      swapPageContent(entry.parsedDoc, href);
      currentImageUrlHint = entry.mainImageUrl || currentImageUrlHint;
      return entry.mainImageUrl;
    });

    runCurtain(PDP_KEY, oldSrc, box, newSrcPromise);
    return true;
  };

  document.addEventListener('click', function (event) {
    var swatchLink = event.target.closest && event.target.closest('.product-color-swatch[href]');
    if (!swatchLink) return;
    var href = swatchLink.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    var handled = runPdpReveal(swatchLink);
    if (!handled) window.location.href = href;
  });

  document.addEventListener(
    'pointerenter',
    function (event) {
      var swatchLink = event.target.closest && event.target.closest('.product-color-swatch[href]');
      if (!swatchLink) return;
      var href = swatchLink.getAttribute('href');
      if (href) fetchProductPage(href);
    },
    true
  );
  document.addEventListener(
    'touchstart',
    function (event) {
      var swatchLink = event.target.closest && event.target.closest('.product-color-swatch[href]');
      if (!swatchLink) return;
      var href = swatchLink.getAttribute('href');
      if (href) fetchProductPage(href);
    },
    { passive: true, capture: true }
  );

  // ---------------------------------------------------------------
  // Case 2: collection-card swatch buttons. A collection page can
  // list many different products' cards side by side; each click is
  // gated by whether it's a .product-color-swatch at all, nothing
  // product-specific. Capturing phase so this runs BEFORE
  // theme.liquid's own non-capturing listener, letting us snapshot
  // the "before" image. We never call preventDefault/
  // stopPropagation: theme.liquid's own handler still receives and
  // fully processes this same click afterward, completely unmodified,
  // for every product — we only add a visual layer on top of it.
  // ---------------------------------------------------------------
  var cardProductJsonCache = (window.__colorSwatchProductsCache = window.__colorSwatchProductsCache || {});

  function fetchCardProductJson(handle) {
    if (cardProductJsonCache[handle]) return Promise.resolve(cardProductJsonCache[handle]);
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed');
        return r.json();
      })
      .then(function (p) {
        cardProductJsonCache[handle] = p;
        return p;
      })
      .catch(function () {
        return null;
      });
  }

  function findCardVisibleImg(card) {
    var desktop = card.querySelector('.card__media-desktop .media img:nth-child(1)');
    if (desktop && desktop.offsetParent !== null) return desktop;
    var mobile = card.querySelector('.card__media-mobile img');
    if (mobile && mobile.offsetParent !== null) return mobile;
    return desktop || mobile || null;
  }

  // Preload on hover/touch, same reasoning as Case 1, and writing into
  // the SAME cache theme.liquid's own handler reads from — see the
  // top-of-file comment on Case 2 for why this benefits the existing
  // swap too, not just this file's own reveal.
  document.addEventListener(
    'pointerenter',
    function (event) {
      var swatch = event.target.closest && event.target.closest('.product-color-swatch');
      if (!swatch || !swatch.closest('.product-card-wrapper')) return;
      var handle = swatch.dataset.productHandle;
      if (handle) fetchCardProductJson(handle);
    },
    true
  );
  document.addEventListener(
    'touchstart',
    function (event) {
      var swatch = event.target.closest && event.target.closest('.product-color-swatch');
      if (!swatch || !swatch.closest('.product-card-wrapper')) return;
      var handle = swatch.dataset.productHandle;
      if (handle) fetchCardProductJson(handle);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'click',
    function (event) {
      var swatch = event.target.closest && event.target.closest('.product-color-swatch');
      if (!swatch) return;

      var card = swatch.closest('.product-card-wrapper');
      if (!card) return; // not inside a card — Case 1 (or default behavior) applies instead

      var handle = swatch.dataset.productHandle;
      if (!handle) return;

      var imgEl = findCardVisibleImg(card);
      if (!imgEl) return; // no image found — fail safe, let the existing swap show its result with no overlay

      var box = snapshotImageBox(imgEl);
      var oldSrc = imgEl.currentSrc || imgEl.src;

      var newSrcPromise = fetchCardProductJson(handle).then(function (product) {
        if (!product) return null;
        var first = product.images && product.images[0];
        if (!first) return null;
        return typeof first === 'string' ? first : first.src || null;
      });

      var swatchContainer = swatch.closest('.product-color-swatches');
      var revealKey = swatchContainer || card;

      runCurtain(revealKey, oldSrc, box, newSrcPromise);
      // No preventDefault/stopPropagation — see comment above.
    },
    true
  );

  // ---------------------------------------------------------------
  // Case 3: genuine Shopify variant color change (same product).
  // See the top-of-file comment for the full explanation and the
  // explicit caveat that this could not be live-tested — no product
  // on this store currently has more than one Color value.
  // ---------------------------------------------------------------
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    var VARIANT_KEY = {};
    var pendingVariantResolve = null;

    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function () {
      var img = findActiveGalleryImg(document);
      if (!img) return;

      var box = snapshotImageBox(img);
      var oldSrc = img.currentSrc || img.src;

      var newSrcPromise = new Promise(function (resolve) {
        pendingVariantResolve = function (newSrc) {
          resolve(newSrc && newSrc !== oldSrc ? newSrc : null);
        };
      });

      runCurtain(VARIANT_KEY, oldSrc, box, newSrcPromise);
    });

    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      if (!pendingVariantResolve) return;
      var resolve = pendingVariantResolve;
      pendingVariantResolve = null;

      // By this point Dawn's own swap (assets/product-info.js's
      // updateMedia -> media-gallery's setActiveMedia) has already run,
      // so the gallery's active image already reflects the new variant
      // — read it straight from the DOM rather than trying to resolve
      // it from the event payload.
      var newImg = findActiveGalleryImg(document);
      resolve(newImg ? newImg.currentSrc || newImg.src : null);
    });
  }

  window.ColorSwatchReveal = {};
})();
